import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Sparkles, Rocket, Target, Zap, ArrowRight, X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';

interface WelcomeTourModalProps {
  onStart: () => void;
  onSkip: () => void;
}

const features = [
  {
    icon: Target,
    title: 'Dashboard Inteligente',
    description: 'Acompanhe métricas e progresso'
  },
  {
    icon: Zap,
    title: 'Ferramentas de IA',
    description: 'Agentes e prompts poderosos'
  },
  {
    icon: Rocket,
    title: 'SmartCheck',
    description: 'Checklists para suas metas'
  }
];

export function WelcomeTourModal({ onStart, onSkip }: WelcomeTourModalProps) {
  const isMobile = useIsMobile();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.9, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 20 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        className="w-full max-w-md"
      >
        <Card className="relative overflow-hidden border-2 border-primary/20 shadow-2xl">
          {/* Close button */}
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-3 right-3 z-10 h-8 w-8"
            onClick={onSkip}
          >
            <X className="w-4 h-4" />
          </Button>

          {/* Gradient background */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-primary/10" />
          
          <CardHeader className={`text-center relative ${isMobile ? 'pt-8 pb-2' : 'pt-8 pb-4'}`}>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="mx-auto mb-4 p-4 rounded-full bg-primary/10 w-fit"
            >
              <Sparkles className={`${isMobile ? 'w-8 h-8' : 'w-10 h-10'} text-primary`} />
            </motion.div>
            
            <CardTitle className={`${isMobile ? 'text-xl' : 'text-2xl'} font-bold`}>
              Bem-vindo ao MarketPro Tools! 🎉
            </CardTitle>
            <CardDescription className={`${isMobile ? 'text-sm' : 'text-base'} mt-2`}>
              Vamos fazer um tour rápido pela plataforma para você conhecer os principais recursos.
            </CardDescription>
          </CardHeader>

          <CardContent className={`relative ${isMobile ? 'px-4 pb-4' : 'px-6 pb-6'} space-y-4`}>
            {/* Features */}
            <div className={`grid ${isMobile ? 'gap-2' : 'gap-3'}`}>
              {features.map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className={`flex items-center gap-3 ${isMobile ? 'p-2' : 'p-3'} rounded-lg bg-muted/50`}
                >
                  <div className={`${isMobile ? 'p-1.5' : 'p-2'} rounded-md bg-primary/10`}>
                    <feature.icon className={`${isMobile ? 'w-4 h-4' : 'w-5 h-5'} text-primary`} />
                  </div>
                  <div>
                    <p className={`font-medium ${isMobile ? 'text-sm' : ''}`}>{feature.title}</p>
                    <p className={`${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Actions */}
            <div className={`flex ${isMobile ? 'flex-col gap-2' : 'gap-3'} pt-2`}>
              <Button
                variant="outline"
                onClick={onSkip}
                className={isMobile ? 'w-full' : 'flex-1'}
              >
                Pular Tour
              </Button>
              <Button
                onClick={onStart}
                className={`${isMobile ? 'w-full' : 'flex-1'} bg-primary hover:bg-primary/90`}
              >
                Começar Tour
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>

            <p className={`text-center ${isMobile ? 'text-xs' : 'text-sm'} text-muted-foreground`}>
              Leva apenas 2 minutos ⏱️
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </motion.div>
  );
}
